/**
 * SETTINGS
 * 測光 / 撮影 / 暗室 / データ / クラウド同期 / アプリ情報
 */

import {
  showScreen, escapeHtml, toast, isDarkroomMode, setDarkroomMode, confirmDialog, barHtml, backBtn, buzz, applyDarkLevel,
} from './ui.js';
import { listShoots } from './store.js';
import { listPresets } from './presets.js';
import { getSyncConfig, setSyncConfig, ensureDeviceId, isSyncConfigured, performFullSync, healthCheck } from './sync.js';
import { getDeviceBaseFov, setDeviceBaseFov } from './data/film-formats.js';
import { prefs } from './prefs.js';
import { testSound, canIgnoreSilentSwitch, beepLevel, setBeepLevel } from './signal.js';

const LAST_SYNC_KEY = 'zn.sync.lastSync';

export function openSettings() {
  showScreen('settings');
  render();
}

function seg(key, opts, cur) {
  return `<div class="seg">${opts.map(([v, t]) => `<button type="button" class="seg__opt ${String(cur) === String(v) ? 'is-on' : ''}" data-${key}="${v}">${t}</button>`).join('')}</div>`;
}
function toggleRow(key, title, desc, on) {
  return `<div class="set-row set-row--flex"><div style="flex:1"><div class="set-row__t">${title}</div><div class="set-row__d">${desc}</div></div><button type="button" class="toggle ${on ? 'is-on' : ''}" data-toggle="${key}" role="switch" aria-checked="${on}" aria-label="${title}"></button></div>`;
}
function head(en, jp) {
  return `<div class="sec-head"><span>${en}</span><span class="sec-head__jp">${jp}</span></div>`;
}
function formatBytes(n) {
  if (n < 1024) return `${n}<small>B</small>`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}<small>KB</small>`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}<small>MB</small>`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)}<small>GB</small>`;
}

async function render() {
  const el = document.getElementById('screen-settings');
  const keepScroll = el.querySelector('.body')?.scrollTop || 0;
  let shoots = [];
  try { shoots = await listShoots({ limit: 1000 }); } catch {}
  const presets = listPresets();
  let persisted = null;
  try { if (navigator.storage?.persisted) persisted = await navigator.storage.persisted(); } catch {}
  const bytes = shoots.reduce((sum, s) => sum + (s.croppedImageBlob?.size || 0) + (s.monoImageBlob?.size || 0) + (s.originalImageBlob?.size || 0), 0);
  const sync = getSyncConfig();
  const last = parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10);
  const evStep = prefs.evStep === 1 ? '1' : prefs.evStep === 0.5 ? '0.5' : '0.333';

  el.innerHTML = `
    ${barHtml(backBtn())}
    <div class="page-head"><div><div class="eyebrow">settings</div><div class="page-title">設定</div></div></div>
    <div class="body"><div style="padding:0 var(--gutter) 40px">
      ${head('metering', '測光')}
      <div class="set-row"><div class="set-row__t">測光値の入力</div><div class="set-row__d">ピン編集で最初に開く方式</div>${seg('meter', [['ev', 'EV 値'], ['sek', 'L-858D']], prefs.meterInput)}</div>
      <div class="set-row"><div class="set-row__t">入力の操作</div><div class="set-row__d">ドラム：上下に回す　ダイヤル：親指で左右に回す</div>${seg('style', [['drum', 'ドラム'], ['arc', 'ダイヤル']], prefs.inputStyle)}</div>
      <div class="set-row"><div class="set-row__t">L-858D の既定の絞り</div><div class="set-row__d">F 優先で使う絞り。ピン編集でいつでも変更できます</div>${seg('ap', [['11', 'f/11 風景'], ['8', 'f/8 人物']], prefs.sekAperture)}</div>
      <div class="set-row"><div class="set-row__t">L-858D のシャッター速度の段</div><div class="set-row__d">メーター側の設定（1 / ⅓ 段）に合わせる</div>${seg('sekstep', [['1', '1 段'], ['1/3', '⅓ 段']], prefs.sekStep)}</div>
      ${toggleRow('tenth', 'L-858D の 1/10 段表示', '「30」の右下の小さな数字（例：1/30 + 0.5 段）も入力する', prefs.sekTenth)}
      <div class="set-row"><div class="set-row__t">EV の刻み</div><div class="set-row__d">EV 入力の − / + で増減する幅</div>${seg('ev', [['1', '1 段'], ['0.5', '½ 段'], ['0.333', '⅓ 段']], evStep)}</div>
      ${toggleRow('as', '絞り・シャッターを併記', 'EV 入力時に「f/8 · 1/30s 相当」を表示', prefs.showApertureSS)}

      ${head('capture', '撮影')}
      <div class="set-row"><div class="set-row__t">iPhone の基準画角</div><div class="set-row__d">レンズに合わせたズーム倍率の基準（35mm 換算）。多くの iPhone は 24mm、SE などは 26mm</div>${seg('fov', [[24, '24mm'], [26, '26mm'], [28, '28mm']], getDeviceBaseFov())}</div>
      ${toggleRow('gps', '位置情報を記録', '撮影地・天気・月齢・太陽の記録に使います', prefs.gps)}

      ${head('darkroom', '暗室')}
      ${toggleRow('autodark', '現像画面で自動的に暗室モード', '現像画面を離れると元のモードに戻ります', prefs.autoDarkroom)}
      ${toggleRow('beep', '攪拌の合図音', '30 秒ごとの短い音（完了音は常に鳴ります）', prefs.agitateBeep)}
      <div class="set-row"><div class="set-row__t">合図の音量</div><div class="set-row__d">選ぶとその音量で試し鳴りします。iPhone の音量ボタンでも調整できます</div>${seg('vol', [['normal', '普通'], ['loud', '大きめ'], ['max', '最大']], beepLevel())}</div>
      <div class="set-row"><button class="btn-line btn-line--block" type="button" data-sound style="height:48px">合図の音を試す（攪拌音 → 完了音）</button><div class="set-row__d" style="margin-top:8px">iPhone は振動に対応していないため、暗室での合図は音だけです。${canIgnoreSilentSwitch() ? '' : 'この端末では消音スイッチがオンだと鳴りません。'}</div></div>
      <div class="set-row"><div class="set-row__t">暗室モードの明るさ</div><div class="set-row__d">赤一色のまま画面全体を暗くします。暗順応を保ちたいときは「最も暗い」</div>${seg('dlevel', [['normal', '標準'], ['dim', '暗め'], ['deep', '最も暗い']], prefs.darkLevel)}</div>
      <div class="set-row"><button class="btn-line btn-line--block" type="button" data-dark style="height:52px;font-size:15px">${isDarkroomMode() ? '通常モードに戻す' : '今すぐ暗室モードに切替'}</button></div>

      ${head('data', 'データ')}
      <div class="set-row" style="display:grid;grid-template-columns:1fr 1fr 1fr">
        <div><div class="big-count">${shoots.length}</div><div class="sub" style="font-size:11px;margin-top:6px">シーン</div></div>
        <div><div class="big-count">${presets.length}</div><div class="sub" style="font-size:11px;margin-top:6px">プリセット</div></div>
        <div><div class="big-count">${formatBytes(bytes)}</div><div class="sub" style="font-size:11px;margin-top:6px">画像</div></div>
      </div>
      <div class="set-row set-row--flex"><div style="flex:1"><div class="set-row__t">保存の永続化</div><div class="set-row__d">${persisted === true ? '端末に、記録を消さないよう求めています（許可済み）。それでもバックアップは別に取ってください' : persisted === false ? 'ブラウザが永続化を許可していません。ホーム画面に追加して使うと許可されやすくなります' : 'この端末では確認できません'}</div></div><span class="badge ${persisted ? 'is-on' : ''}">${persisted === true ? '有効' : persisted === false ? '未許可' : '—'}</span></div>
      <button class="link-row" type="button" data-export><div class="link-row__body"><div class="set-row__t">JSON を書き出す</div><div class="set-row__d" style="margin:0">プリセットとシーン（画像本体は含まない）</div></div><svg class="i" width="16" height="16" style="color:var(--color-muted)"><use href="#i-chev"/></svg></button>
      <button class="link-row" type="button" data-cache><div class="link-row__body"><div class="set-row__t">オフライン用キャッシュを消去</div><div class="set-row__d" style="margin:0">記録したデータは消えません</div></div><svg class="i" width="16" height="16" style="color:var(--color-muted)"><use href="#i-chev"/></svg></button>

      ${head('sync', 'クラウド同期 · 任意')}
      <p class="set-row__d" style="margin-top:12px">Cloudflare Workers + D1 + R2 への手動バックアップとデバイス間同期。AI に質問もこの設定を使います。</p>
      <label class="field"><span class="field__label">Worker の URL</span><input class="input en" id="sync-endpoint" type="url" value="${escapeHtml(sync.endpoint)}" placeholder="https://zone-notes-worker.example.workers.dev" /></label>
      <label class="field"><span class="field__label">デバイストークン</span><input class="input en" id="sync-token" type="password" autocomplete="off" value="${escapeHtml(sync.token)}" placeholder="Cloudflare で設定した DEVICE_TOKEN" /></label>
      <div class="en sub" style="display:flex;justify-content:space-between;gap:12px;margin-top:12px;font-size:12px"><span style="font-family:var(--font-jp-sans)">デバイス ID</span><span style="word-break:break-all;text-align:right">${escapeHtml(sync.deviceId || '（未生成）')}</span></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:16px">
        <button class="btn-line" type="button" data-sync-save style="height:52px">保存</button>
        <button class="btn-line" type="button" data-sync-test style="height:52px">接続テスト</button>
      </div>
      <button class="btn-primary ${isSyncConfigured() ? '' : 'is-disabled'}" type="button" data-sync-now style="margin-top:8px;height:58px">今すぐ同期</button>
      <div class="en sub" style="font-size:11px;margin-top:8px"><span style="font-family:var(--font-jp-sans)">最終同期</span> ${last ? escapeHtml(new Date(last).toLocaleString('ja-JP')) : '—'}</div>

      <div class="sec-head" style="margin-top:28px"><span>about</span><span class="en" style="font-size:13px;letter-spacing:0;color:var(--color-ink)">Zone Notes v0.3.0</span></div>
      <p class="set-row__d">中判フィルム × ゾーンシステム フィールドノート</p>
    </div></div>
  `;

  el.querySelector('.body').scrollTop = keepScroll;
  const bindSeg = (key, fn) => el.querySelectorAll(`[data-${key}]`).forEach((b) => b.addEventListener('click', () => { fn(b.dataset[key]); buzz(6); render(); }));
  bindSeg('meter', (v) => { prefs.meterInput = v; });
  bindSeg('style', (v) => { prefs.inputStyle = v; });
  bindSeg('ap', (v) => { prefs.sekAperture = v; });
  bindSeg('sekstep', (v) => { prefs.sekStep = v; });
  bindSeg('ev', (v) => { prefs.evStep = Number(v); });
  bindSeg('fov', (v) => { setDeviceBaseFov(parseFloat(v)); });
  bindSeg('vol', (v) => { setBeepLevel(v); testSound(); });
  bindSeg('dlevel', (v) => { prefs.darkLevel = v; applyDarkLevel(); if (!isDarkroomMode()) toast('暗室モードのときに反映されます'); });
  el.querySelectorAll('[data-toggle]').forEach((b) => b.addEventListener('click', () => {
    const k = b.dataset.toggle;
    const map = { tenth: 'sekTenth', as: 'showApertureSS', gps: 'gps', autodark: 'autoDarkroom', beep: 'agitateBeep' };
    prefs[map[k]] = !prefs[map[k]];
    buzz(6);
    render();
  }));
  el.querySelector('[data-dark]').addEventListener('click', () => {
    setDarkroomMode(!isDarkroomMode(), { persist: true });
    render();
  });
  el.querySelector('[data-sound]').addEventListener('click', () => testSound());
  el.querySelector('[data-export]').addEventListener('click', exportJson);
  el.querySelector('[data-cache]').addEventListener('click', async () => {
    const ok = await confirmDialog({ title: 'キャッシュを消去しますか？', body: 'オフライン用に保存したアプリのファイルを消去します。記録したシーン・プリセットは消えません。', ok: '消去' });
    if (!ok) return;
    if ('caches' in window) {
      const keys = await caches.keys();
      for (const k of keys) await caches.delete(k);
    }
    toast('キャッシュを消去しました');
  });
  el.querySelector('[data-sync-save]').addEventListener('click', () => {
    const endpoint = el.querySelector('#sync-endpoint').value.trim();
    const token = el.querySelector('#sync-token').value.trim();
    if (!endpoint || !token) { toast('URL とトークンを入力してください'); return; }
    if (!/^https:\/\//.test(endpoint) && !/^http:\/\/(localhost|127\.0\.0\.1)/.test(endpoint)) { toast('URL は https:// で始めてください'); return; }
    setSyncConfig({ endpoint, token });
    ensureDeviceId();
    toast('同期設定を保存しました');
    render();
  });
  el.querySelector('[data-sync-test]').addEventListener('click', async () => {
    toast('接続を確認しています…');
    const ok = await healthCheck();
    toast(ok ? '接続成功' : '接続失敗：URL とトークンを確認してください');
  });
  el.querySelector('[data-sync-now]').addEventListener('click', async () => {
    if (!isSyncConfigured()) { toast('URL とトークンを保存してください'); return; }
    toast('同期しています…');
    try {
      const r = await performFullSync();
      toast(r.failed
        ? `一部を送れませんでした（${r.failed} 件）。次回もう一度送ります: pull ${r.pulled || 0} / push ${r.pushed || 0}`
        : `同期完了: pull ${r.pulled || 0} / push ${r.pushed || 0}`);
      render();
    } catch (e) {
      console.warn('[sync] failed:', e);
      toast('同期に失敗しました');
    }
  });
}

async function exportJson() {
  const shoots = await listShoots({ limit: 9999 });
  const presets = listPresets();
  const data = {
    exportedAt: new Date().toISOString(),
    schemaVersion: 1,
    presets,
    shoots: shoots.map((s) => ({
      ...s,
      originalImageBlob: undefined,
      croppedImageBlob: undefined,
      monoImageBlob: s.monoImageBlob ? `[blob: ${s.monoImageBlob.size}B]` : null,
    })),
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const d = new Date();
  a.href = url;
  a.download = `zone-notes-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast(`${a.download} を書き出しました（画像は含みません）`);
}
